import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { 
  Calendar, Clock, User, Mail, Phone, 
  CheckCircle, XCircle, Search, 
  ChevronLeft, ChevronRight, Eye, X, History, CalendarDays,
  Camera, Mic, Film, Clapperboard, MapPin, MessageCircle,
  Shield, Loader2, FileText
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Credentials
const ADMIN_USER = 'Lemeilleurpodacat';
const ADMIN_PASSWORD = '@dioewndiwoedneiowdenwiodew123';

export default function AdminDashboard() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  
  // 2FA state
  const [loginStep, setLoginStep] = useState('credentials'); // 'credentials' | '2fa'
  const [twoFACode, setTwoFACode] = useState(['', '', '', '', '', '']);
  const [twoFASessionId, setTwoFASessionId] = useState('');
  const [twoFASending, setTwoFASending] = useState(false);
  const [twoFAVerifying, setTwoFAVerifying] = useState(false);
  const [twoFAResendTimer, setTwoFAResendTimer] = useState(0);
  const codeInputRefs = useRef([]);
  
  const [bookings, setBookings] = useState([]);
  const [serviceInquiries, setServiceInquiries] = useState([]);
  const [customPayments, setCustomPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('bookings');
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPastBookings, setShowPastBookings] = useState(false);
  const [viewMode, setViewMode] = useState('list');
  const [calendarDate, setCalendarDate] = useState(new Date());

  // CRM state
  const [clients, setClients] = useState([]);
  const [clientSearch, setClientSearch] = useState('');
  const [clientFilter, setClientFilter] = useState('all');
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportCode, setExportCode] = useState('');
  const [exporting, setExporting] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (loginData.username !== ADMIN_USER || loginData.password !== ADMIN_PASSWORD) {
      setLoginError('Invalid credentials');
      return;
    }
    setLoginError('');
    setTwoFASending(true);
    try {
      const res = await axios.post(`${API}/admin/send-2fa`, {
        username: loginData.username,
        password: loginData.password
      });
      setTwoFASessionId(res.data.session_id);
      setLoginStep('2fa');
      setTwoFAResendTimer(60);
      toast.success('Code de vérification envoyé par email');
    } catch (err) {
      setLoginError(err.response?.data?.detail || 'Error sending verification code');
    } finally {
      setTwoFASending(false);
    }
  };

  const handleResend2FA = async () => {
    if (twoFAResendTimer > 0) return;
    setTwoFASending(true);
    try {
      const res = await axios.post(`${API}/admin/send-2fa`, {
        username: loginData.username,
        password: loginData.password
      });
      setTwoFASessionId(res.data.session_id);
      setTwoFACode(['', '', '', '', '', '']);
      setTwoFAResendTimer(60);
      toast.success('Nouveau code envoyé');
    } catch (err) {
      toast.error('Erreur lors du renvoi');
    } finally {
      setTwoFASending(false);
    }
  };

  const handleVerify2FA = async (overrideCode) => {
    const code = overrideCode || twoFACode.join('');
    if (code.length !== 6) return;
    setTwoFAVerifying(true);
    setLoginError('');
    try {
      await axios.post(`${API}/admin/verify-2fa`, {
        code,
        session_id: twoFASessionId
      });
      setIsAuthenticated(true);
      if (rememberMe) {
        const expiry = Date.now() + 7 * 24 * 60 * 60 * 1000;
        localStorage.setItem('admin_auth', 'true');
        localStorage.setItem('admin_auth_expiry', expiry.toString());
      } else {
        sessionStorage.setItem('admin_auth', 'true');
      }
      toast.success('Connexion réussie');
    } catch (err) {
      setLoginError(err.response?.data?.detail || 'Code invalide');
      setTwoFACode(['', '', '', '', '', '']);
      codeInputRefs.current[0]?.focus();
    } finally {
      setTwoFAVerifying(false);
    }
  };

  const handleCodeChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newCode = [...twoFACode];
    newCode[index] = value.slice(-1);
    setTwoFACode(newCode);
    if (value && index < 5) {
      codeInputRefs.current[index + 1]?.focus();
    }
    if (newCode.every(d => d !== '') && newCode.join('').length === 6) {
      const fullCode = newCode.join('');
      setTimeout(() => handleVerify2FA(fullCode), 100);
    }
  };

  const handleCodeKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !twoFACode[index] && index > 0) {
      codeInputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'Enter') {
      handleVerify2FA();
    }
  };

  const handleCodePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      setTwoFACode(pasted.split(''));
      codeInputRefs.current[5]?.focus();
      setTimeout(() => handleVerify2FA(pasted), 100);
    }
  };

  // Resend timer
  useEffect(() => {
    if (twoFAResendTimer <= 0) return;
    const t = setTimeout(() => setTwoFAResendTimer(twoFAResendTimer - 1), 1000);
    return () => clearTimeout(t);
  }, [twoFAResendTimer]);

  useEffect(() => {
    // Check localStorage first (remember me)
    const localAuth = localStorage.getItem('admin_auth');
    const expiry = localStorage.getItem('admin_auth_expiry');
    if (localAuth === 'true' && expiry && Date.now() < parseInt(expiry)) {
      setIsAuthenticated(true);
      return;
    }
    // Clear expired localStorage
    if (localAuth) {
      localStorage.removeItem('admin_auth');
      localStorage.removeItem('admin_auth_expiry');
    }
    // Check sessionStorage
    if (sessionStorage.getItem('admin_auth') === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchBookings();
      fetchServiceInquiries();
      fetchCustomPayments();
      fetchClients();
    }
  }, [isAuthenticated]);

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/bookings`);
      setBookings(response.data);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      toast.error('Error loading bookings');
    } finally {
      setLoading(false);
    }
  };

  const fetchServiceInquiries = async () => {
    try {
      const response = await axios.get(`${API}/service-inquiries`);
      setServiceInquiries(response.data);
    } catch (error) {
      console.error('Error fetching service inquiries:', error);
    }
  };

  const fetchCustomPayments = async () => {
    try {
      const response = await axios.get(`${API}/custom-payments`);
      setCustomPayments(response.data);
    } catch (error) {
      console.error('Error fetching custom payments:', error);
    }
  };

  const refreshAll = () => {
    fetchBookings();
    fetchServiceInquiries();
    fetchCustomPayments();
    fetchClients();
  };

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/admin/clients`);
      setClients(response.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const handleExportClients = async () => {
    if (exportCode !== '@Beaucoup1144') {
      toast.error('Code incorrect');
      return;
    }
    setExporting(true);
    try {
      const response = await axios.post(`${API}/admin/export-clients`, { code: exportCode }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'clients_export.csv';
      a.click();
      window.URL.revokeObjectURL(url);
      setShowExportModal(false);
      setExportCode('');
      toast.success('Export téléchargé');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur export');
    } finally {
      setExporting(false);
    }
  };

  const updateBookingStatus = async (bookingId, newStatus) => {
    try {
      await axios.patch(`${API}/bookings/${bookingId}/status`, { 
        payment_status: newStatus 
      });
      toast.success('Status updated');
      fetchBookings();
      setShowEditModal(false);
    } catch (error) {
      console.error('Error updating booking:', error);
      toast.error('Error updating');
    }
  };

  // Cancel booking confirmation
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelBookingId, setCancelBookingId] = useState(null);

  const cancelBooking = async (bookingId) => {
    setCancelBookingId(bookingId);
    setShowCancelModal(true);
  };

  const confirmCancelBooking = async () => {
    try {
      await axios.patch(`${API}/bookings/${cancelBookingId}/status`, { 
        payment_status: 'cancelled' 
      });
      toast.success('Booking cancelled');
      fetchBookings();
    } catch (error) {
      console.error('Error cancelling booking:', error);
      toast.error('Error cancelling');
    } finally {
      setShowCancelModal(false);
      setCancelBookingId(null);
    }
  };

  const updateInquiryStatus = async (inquiryId, statusUpdate) => {
    try {
      await axios.patch(`${API}/service-inquiries/${inquiryId}/status`, statusUpdate);
      toast.success('Status updated');
      fetchServiceInquiries();
      setShowEditModal(false);
    } catch (error) {
      console.error('Error updating inquiry:', error);
      toast.error('Error updating');
    }
  };

  const formatIDR = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const isUpcoming = (booking) => {
    const bookingDate = new Date(booking.date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return bookingDate >= today && booking.payment_status !== 'cancelled';
  };

  const getStatusBadge = (status) => {
    const styles = {
      paid: 'bg-green-500/20 text-green-400 border-green-500/30',
      pending: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
      pending_payment: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      cancelled: 'bg-red-500/20 text-red-400 border-red-500/30'
    };
    const labels = {
      paid: 'Paid',
      pending: 'Pending payment',
      pending_payment: 'Pay later',
      cancelled: 'Cancelled'
    };
    return (
      <span className={`px-2 py-1 text-xs font-semibold border rounded ${styles[status] || styles.pending}`}>
        {labels[status] || status}
      </span>
    );
  };

  // Filter bookings
  const filteredBookings = bookings
    .filter(b => showPastBookings || isUpcoming(b))
    .filter(b => filter === 'all' || b.payment_status === filter)
    .filter(b => 
      searchTerm === '' || 
      b.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      b.customer_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      b.customer_phone?.includes(searchTerm)
    )
    .sort((a, b) => new Date(a.date) - new Date(b.date));

  // Calendar helpers
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    return { daysInMonth, startingDay };
  };

  const getBookingsForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return bookings.filter(b => b.date === dateStr && b.payment_status !== 'cancelled');
  };

  const renderCalendar = () => {
    const { daysInMonth, startingDay } = getDaysInMonth(calendarDate);
    const days = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Empty cells for days before the first day of the month
    for (let i = 0; i < startingDay; i++) {
      days.push(<div key={`empty-${i}`} className="p-2 min-h-[80px]" />);
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const currentDate = new Date(calendarDate.getFullYear(), calendarDate.getMonth(), day);
      const dayBookings = getBookingsForDate(currentDate);
      const isToday = currentDate.getTime() === today.getTime();
      const isPast = currentDate < today;

      days.push(
        <div
          key={day}
          className={`p-2 min-h-[80px] border border-border ${
            isToday ? 'bg-primary/10 border-primary' : isPast ? 'bg-muted/30 opacity-50' : ''
          }`}
        >
          <div className={`text-sm font-semibold mb-1 ${isToday ? 'text-primary' : ''}`}>
            {day}
          </div>
          <div className="space-y-1">
            {dayBookings.slice(0, 3).map((booking, idx) => (
              <div
                key={idx}
                onClick={() => {
                  setSelectedBooking(booking);
                  setShowEditModal(true);
                }}
                className={`text-xs p-1 rounded cursor-pointer truncate ${
                  booking.payment_status === 'paid' 
                    ? 'bg-green-500/20 text-green-400' 
                    : 'bg-amber-500/20 text-amber-400'
                }`}
                title={`${booking.customer_name} - ${booking.start_time}`}
              >
                {booking.start_time} {booking.customer_name?.split(' ')[0]}
              </div>
            ))}
            {dayBookings.length > 3 && (
              <div className="text-xs text-foreground-muted">
                +{dayBookings.length - 3} autres
              </div>
            )}
          </div>
        </div>
      );
    }

    return days;
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // Stats
  const upcomingBookings = bookings.filter(isUpcoming);
  const stats = {
    total: showPastBookings ? bookings.length : upcomingBookings.length,
    paid: (showPastBookings ? bookings : upcomingBookings).filter(b => b.payment_status === 'paid').length,
    pending: (showPastBookings ? bookings : upcomingBookings).filter(b => b.payment_status === 'pending' || b.payment_status === 'pending_payment').length,
    cancelled: (showPastBookings ? bookings : upcomingBookings).filter(b => b.payment_status === 'cancelled').length,
    inquiries: serviceInquiries.length,
    customPayments: customPayments.length
  };

  const serviceNames = {
    studio_rental: 'Studio Rental',
    photo_studio: 'Photo Studio',
    live_streaming: 'Live Streaming',
    content_retreat: 'Content Retreat',
    online_course: 'Online Course',
    mobile_kit: 'Mobile Kit',
    monthly_plan: 'Monthly Plan'
  };

  // Filter service inquiries
  const filteredInquiries = serviceInquiries
    .filter(i => filter === 'all' || i.payment_status === filter || i.status === filter)
    .filter(i =>
      searchTerm === '' ||
      i.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      i.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      i.phone?.includes(searchTerm)
    );

  // Logout handler
  const handleLogout = () => {
    setIsAuthenticated(false);
    setLoginStep('credentials');
    setLoginData({ username: '', password: '' });
    setTwoFACode(['', '', '', '', '', '']);
    setTwoFASessionId('');
    sessionStorage.removeItem('admin_auth');
    localStorage.removeItem('admin_auth');
    localStorage.removeItem('admin_auth_expiry');
  };

  // Reset Database
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetCode, setResetCode] = useState('');
  const [resetting, setResetting] = useState(false);

  const handleResetDB = async () => {
    if (resetCode !== '@Beaucoup1144') {
      toast.error('Code incorrect');
      return;
    }
    setResetting(true);
    try {
      const res = await axios.post(`${API}/admin/reset-database`, { code: resetCode });
      toast.success(res.data.message || 'Base de données vidée');
      setShowResetModal(false);
      setResetCode('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setResetting(false);
    }
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" data-testid="admin-login">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md p-8 bg-background border border-border"
        >
          {loginStep === 'credentials' ? (
            <>
              <h1 className="text-2xl font-heading font-semibold mb-2 text-center">Admin Dashboard</h1>
              <p className="text-sm text-foreground-muted text-center mb-8">Canggu Podcast Studio</p>
              
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Username</label>
                  <input
                    type="text"
                    value={loginData.username}
                    onChange={(e) => setLoginData({ ...loginData, username: e.target.value })}
                    className="w-full px-4 py-3 bg-muted border border-border text-foreground focus:border-primary focus:outline-none"
                    data-testid="admin-username"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Password</label>
                  <input
                    type="password"
                    value={loginData.password}
                    onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                    className="w-full px-4 py-3 bg-muted border border-border text-foreground focus:border-primary focus:outline-none"
                    data-testid="admin-password"
                  />
                </div>

                {/* Remember Me */}
                <label 
                  className="flex items-center gap-3 cursor-pointer select-none" 
                  data-testid="remember-me"
                  onClick={() => setRememberMe(!rememberMe)}
                >
                  <div
                    className={`w-5 h-5 border-2 flex items-center justify-center transition-colors ${
                      rememberMe ? 'bg-primary border-primary' : 'border-border hover:border-primary/50'
                    }`}
                  >
                    {rememberMe && <CheckCircle className="w-3.5 h-3.5 text-primary-foreground" />}
                  </div>
                  <span className="text-sm text-foreground-muted">Rester connecté pendant 7 jours</span>
                </label>

                {loginError && (
                  <p className="text-red-500 text-sm" data-testid="login-error">{loginError}</p>
                )}
                <button
                  type="submit"
                  disabled={twoFASending}
                  className="w-full py-3 bg-primary text-primary-foreground font-semibold hover:scale-[0.98] transition-transform flex items-center justify-center gap-2 disabled:opacity-60"
                  data-testid="admin-login-btn"
                >
                  {twoFASending ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Envoi du code...
                    </>
                  ) : (
                    'Login'
                  )}
                </button>
              </form>
            </>
          ) : (
            <>
              {/* 2FA Code Screen */}
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 border border-primary/30 rounded-full mb-4">
                  <Shield className="w-8 h-8 text-primary" />
                </div>
                <h1 className="text-2xl font-heading font-semibold mb-2">Vérification 2FA</h1>
                <p className="text-sm text-foreground-muted">
                  Un code a été envoyé à votre email
                </p>
              </div>
              
              {/* 6-digit code input */}
              <div className="flex justify-center gap-3 mb-6" onPaste={handleCodePaste}>
                {twoFACode.map((digit, i) => (
                  <input
                    key={i}
                    ref={el => codeInputRefs.current[i] = el}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleCodeChange(i, e.target.value)}
                    onKeyDown={(e) => handleCodeKeyDown(i, e)}
                    className="w-12 h-14 text-center text-2xl font-bold bg-muted border-2 border-border text-foreground focus:border-primary focus:outline-none transition-colors"
                    data-testid={`2fa-digit-${i}`}
                  />
                ))}
              </div>

              {loginError && (
                <p className="text-red-500 text-sm text-center mb-4" data-testid="2fa-error">{loginError}</p>
              )}

              <button
                onClick={handleVerify2FA}
                disabled={twoFAVerifying || twoFACode.join('').length !== 6}
                className="w-full py-3 bg-primary text-primary-foreground font-semibold hover:scale-[0.98] transition-transform flex items-center justify-center gap-2 disabled:opacity-60 mb-4"
                data-testid="verify-2fa-btn"
              >
                {twoFAVerifying ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Vérification...
                  </>
                ) : (
                  'Vérifier'
                )}
              </button>

              <div className="text-center space-y-2">
                <button
                  onClick={handleResend2FA}
                  disabled={twoFAResendTimer > 0 || twoFASending}
                  className="text-sm text-primary hover:underline disabled:text-foreground-muted disabled:no-underline"
                  data-testid="resend-2fa-btn"
                >
                  {twoFAResendTimer > 0 ? `Renvoyer le code (${twoFAResendTimer}s)` : 'Renvoyer le code'}
                </button>
                <br />
                <button
                  onClick={() => { setLoginStep('credentials'); setLoginError(''); setTwoFACode(['','','','','','']); }}
                  className="text-sm text-foreground-muted hover:text-foreground"
                  data-testid="back-to-login-btn"
                >
                  Retour
                </button>
              </div>
            </>
          )}
        </motion.div>
      </div>
    );
  }

  // Dashboard
  return (
    <div className="min-h-screen py-8 px-6 md:px-12" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-heading font-semibold">Admin Dashboard</h1>
            <p className="text-foreground-muted">
              {showPastBookings ? 'All bookings' : 'Upcoming bookings'}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowResetModal(true)}
              className="px-4 py-2 border border-red-500/30 text-red-400 hover:bg-red-500/10 hover:border-red-500 transition-colors text-sm"
              data-testid="reset-db-btn"
            >
              Reset Database
            </button>
            <button
              onClick={handleLogout}
              className="px-4 py-2 border border-border text-foreground-muted hover:text-foreground hover:border-foreground transition-colors text-sm"
              data-testid="admin-logout-btn"
            >
              Logout
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-8">
          <div className="p-4 bg-muted/30 border border-border">
            <p className="text-xs text-foreground-muted uppercase tracking-wider mb-1">Total</p>
            <p className="text-2xl font-bold">{stats.total}</p>
          </div>
          <div className="p-4 bg-green-500/10 border border-green-500/30">
            <p className="text-xs text-green-400 uppercase tracking-wider mb-1">Paid</p>
            <p className="text-2xl font-bold text-green-400">{stats.paid}</p>
          </div>
          <div className="p-4 bg-amber-500/10 border border-amber-500/30">
            <p className="text-xs text-amber-400 uppercase tracking-wider mb-1">Pending</p>
            <p className="text-2xl font-bold text-amber-400">{stats.pending}</p>
          </div>
          <div className="p-4 bg-red-500/10 border border-red-500/30">
            <p className="text-xs text-red-400 uppercase tracking-wider mb-1">Cancelled</p>
            <p className="text-2xl font-bold text-red-400">{stats.cancelled}</p>
          </div>
          <div className="p-4 bg-purple-500/10 border border-purple-500/30">
            <p className="text-xs text-purple-400 uppercase tracking-wider mb-1">Service Inquiries</p>
            <p className="text-2xl font-bold text-purple-400">{stats.inquiries}</p>
          </div>
          <div className="p-4 bg-cyan-500/10 border border-cyan-500/30">
            <p className="text-xs text-cyan-400 uppercase tracking-wider mb-1">Custom Payments</p>
            <p className="text-2xl font-bold text-cyan-400">{stats.customPayments}</p>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex border-b border-border mb-6">
          <button
            onClick={() => setActiveTab('bookings')}
            className={`px-6 py-3 font-semibold transition-colors border-b-2 -mb-px ${
              activeTab === 'bookings' ? 'border-primary text-primary' : 'border-transparent text-foreground-muted hover:text-foreground'
            }`}
            data-testid="tab-bookings"
          >
            Bookings ({bookings.length})
          </button>
          <button
            onClick={() => setActiveTab('inquiries')}
            className={`px-6 py-3 font-semibold transition-colors border-b-2 -mb-px ${
              activeTab === 'inquiries' ? 'border-primary text-primary' : 'border-transparent text-foreground-muted hover:text-foreground'
            }`}
            data-testid="tab-inquiries"
          >
            Service Inquiries ({serviceInquiries.length})
          </button>
          <button
            onClick={() => setActiveTab('payments')}
            className={`px-6 py-3 font-semibold transition-colors border-b-2 -mb-px ${
              activeTab === 'payments' ? 'border-primary text-primary' : 'border-transparent text-foreground-muted hover:text-foreground'
            }`}
            data-testid="tab-payments"
          >
            Custom Payments ({customPayments.length})
          </button>
          <button
            onClick={() => setActiveTab('crm')}
            className={`px-6 py-3 font-semibold transition-colors border-b-2 -mb-px ${
              activeTab === 'crm' ? 'border-primary text-primary' : 'border-transparent text-foreground-muted hover:text-foreground'
            }`}
            data-testid="tab-crm"
          >
            CRM ({clients.length})
          </button>
        </div>

        {/* View Toggle & Filters - Only for Bookings */}
        {activeTab === 'bookings' && (
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          {/* View Mode Toggle */}
          <div className="flex border border-border">
            <button
              onClick={() => setViewMode('list')}
              className={`px-4 py-2 flex items-center gap-2 transition-colors ${
                viewMode === 'list' ? 'bg-primary text-primary-foreground' : 'text-foreground-muted hover:text-foreground'
              }`}
            >
              <Search className="w-4 h-4" />
              List
            </button>
            <button
              onClick={() => setViewMode('calendar')}
              className={`px-4 py-2 flex items-center gap-2 transition-colors ${
                viewMode === 'calendar' ? 'bg-primary text-primary-foreground' : 'text-foreground-muted hover:text-foreground'
              }`}
            >
              <CalendarDays className="w-4 h-4" />
              Calendar
            </button>
          </div>

          {/* Past Bookings Toggle */}
          <button
            onClick={() => setShowPastBookings(!showPastBookings)}
            className={`px-4 py-2 flex items-center gap-2 border transition-colors ${
              showPastBookings 
                ? 'bg-primary/10 border-primary text-primary' 
                : 'border-border text-foreground-muted hover:text-foreground'
            }`}
            data-testid="toggle-past-bookings"
          >
            <History className="w-4 h-4" />
            {showPastBookings ? 'Hide past' : 'View past bookings'}
          </button>

          {viewMode === 'list' && (
            <>
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-foreground-muted" />
                <input
                  type="text"
                  placeholder="Search by name, email or phone..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-muted border border-border text-foreground focus:border-primary focus:outline-none"
                  data-testid="admin-search"
                />
              </div>
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="px-4 py-2 bg-muted border border-border text-foreground focus:border-primary focus:outline-none"
                data-testid="admin-filter"
              >
                <option value="all">All statuses</option>
                <option value="paid">Paid</option>
                <option value="pending">Pending</option>
                <option value="pending_payment">Pay later</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </>
          )}
          
          <button
            onClick={refreshAll}
            className="px-4 py-2 bg-primary text-primary-foreground font-semibold hover:scale-[0.98] transition-transform"
          >
            Refresh
          </button>
        </div>
        )}

        {/* Calendar View */}
        {activeTab === 'bookings' && viewMode === 'calendar' && (
          <div className="mb-8">
            {/* Calendar Header */}
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={() => setCalendarDate(new Date(calendarDate.getFullYear(), calendarDate.getMonth() - 1, 1))}
                className="p-2 hover:bg-muted rounded transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <h2 className="text-xl font-heading font-semibold">
                {monthNames[calendarDate.getMonth()]} {calendarDate.getFullYear()}
              </h2>
              <button
                onClick={() => setCalendarDate(new Date(calendarDate.getFullYear(), calendarDate.getMonth() + 1, 1))}
                className="p-2 hover:bg-muted rounded transition-colors"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>

            {/* Calendar Grid */}
            <div className="border border-border">
              {/* Days of week header */}
              <div className="grid grid-cols-7 bg-muted/50">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                  <div key={day} className="p-2 text-center text-xs font-semibold text-foreground-muted uppercase">
                    {day}
                  </div>
                ))}
              </div>
              {/* Calendar days */}
              <div className="grid grid-cols-7">
                {renderCalendar()}
              </div>
            </div>

            {/* Legend */}
            <div className="flex gap-4 mt-4 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-green-500/20 border border-green-500/30 rounded" />
                <span className="text-foreground-muted">Paid</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-amber-500/20 border border-amber-500/30 rounded" />
                <span className="text-foreground-muted">Pending</span>
              </div>
            </div>
          </div>
        )}

        {/* List View */}
        {activeTab === 'bookings' && viewMode === 'list' && (
          <>
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4" />
                <p className="text-foreground-muted">Loading...</p>
              </div>
            ) : filteredBookings.length === 0 ? (
              <div className="text-center py-12 border border-border">
                <Calendar className="w-12 h-12 text-foreground-muted mx-auto mb-4" />
                <p className="text-foreground-muted">
                  {showPastBookings ? 'No bookings found' : 'No upcoming bookings'}
                </p>
              </div>
            ) : (
              <div className="border border-border overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-foreground-muted">Client</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-foreground-muted">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-foreground-muted">Studio</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-foreground-muted">Amount</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-foreground-muted">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-foreground-muted">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {filteredBookings.map((booking) => (
                      <tr key={booking.id} className="hover:bg-muted/30 transition-colors">
                        <td className="px-4 py-4">
                          <div>
                            <p className="font-medium">{booking.customer_name}</p>
                            <p className="text-xs text-foreground-muted">{booking.customer_email}</p>
                            <p className="text-xs text-foreground-muted">{booking.customer_phone}</p>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-primary" />
                            <div>
                              <p className="font-medium">{formatDate(booking.date)}</p>
                              <p className="text-xs text-foreground-muted">{booking.start_time} • {booking.duration_hours}h</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <span className="text-sm">{booking.studio_id}</span>
                        </td>
                        <td className="px-4 py-4">
                          <span className="font-semibold text-primary">{formatIDR(booking.total_price)}</span>
                        </td>
                        <td className="px-4 py-4">
                          {getStatusBadge(booking.payment_status)}
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => {
                                setSelectedBooking(booking);
                                setShowEditModal(true);
                              }}
                              className="p-2 hover:bg-primary/10 text-foreground-muted hover:text-primary transition-colors rounded"
                              title="Voir détails"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => {
                                const url = `${API}/admin/invoice/${booking.id}`;
                                window.open(url, '_blank');
                              }}
                              className="p-2 hover:bg-primary/10 text-foreground-muted hover:text-primary transition-colors rounded"
                              title="Télécharger facture"
                              data-testid={`invoice-btn-${booking.id}`}
                            >
                              <FileText className="w-4 h-4" />
                            </button>
                            {booking.payment_status !== 'cancelled' && (
                              <button
                                onClick={() => cancelBooking(booking.id)}
                                className="p-2 hover:bg-red-500/10 text-foreground-muted hover:text-red-500 transition-colors rounded"
                                title="Annuler"
                              >
                                <XCircle className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* Service Inquiries Tab */}
        {activeTab === 'inquiries' && (
          <>
            <div className="flex flex-col md:flex-row gap-4 mb-6">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-foreground-muted" />
                <input
                  type="text"
                  placeholder="Search by name, email or phone..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-muted border border-border text-foreground focus:border-primary focus:outline-none"
                />
              </div>
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="px-4 py-2 bg-muted border border-border text-foreground focus:border-primary focus:outline-none"
              >
                <option value="all">All statuses</option>
                <option value="paid">Paid</option>
                <option value="pending">Pending</option>
                <option value="confirmed">Confirmed</option>
                <option value="cancelled">Cancelled</option>
              </select>
              <button onClick={refreshAll} className="px-4 py-2 bg-primary text-primary-foreground font-semibold hover:scale-[0.98] transition-transform">
                Refresh
              </button>
            </div>

            {filteredInquiries.length === 0 ? (
              <div className="text-center py-12 border border-border">
                <Calendar className="w-12 h-12 text-foreground-muted mx-auto mb-4" />
                <p className="text-foreground-muted">No service inquiries found</p>
              </div>
            ) : (
              <div className="border border-border overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-foreground-muted">Client</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-foreground-muted">Service</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-foreground-muted">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-foreground-muted">Amount</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-foreground-muted">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-foreground-muted">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {filteredInquiries.map((inquiry) => (
                      <tr key={inquiry.id} className="hover:bg-muted/30 transition-colors">
                        <td className="px-4 py-4">
                          <div>
                            <p className="font-medium">{inquiry.full_name}</p>
                            <p className="text-xs text-foreground-muted">{inquiry.email}</p>
                            <p className="text-xs text-foreground-muted">{inquiry.phone}</p>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <span className="px-2 py-1 text-xs font-semibold bg-purple-500/20 text-purple-400 border border-purple-500/30 rounded">
                            {serviceNames[inquiry.service_type] || inquiry.service_type}
                          </span>
                          {inquiry.selected_pack && (
                            <p className="text-xs text-foreground-muted mt-1">{inquiry.selected_pack}</p>
                          )}
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-primary" />
                            <div>
                              <p className="font-medium">{inquiry.preferred_date ? formatDate(inquiry.preferred_date) : 'TBD'}</p>
                              <p className="text-xs text-foreground-muted">{inquiry.preferred_time || ''}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <span className="font-semibold text-primary">{formatIDR(inquiry.total_price || 0)}</span>
                        </td>
                        <td className="px-4 py-4">
                          {getStatusBadge(inquiry.payment_status || inquiry.status)}
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => {
                                setSelectedBooking({ ...inquiry, _isInquiry: true });
                                setShowEditModal(true);
                              }}
                              className="p-2 hover:bg-primary/10 text-foreground-muted hover:text-primary transition-colors rounded"
                              title="View details"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            {inquiry.contact_method === 'whatsapp' && inquiry.phone && (
                              <a
                                href={`https://wa.me/${inquiry.phone.replace(/[^0-9]/g, '')}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-2 hover:bg-green-500/10 text-foreground-muted hover:text-green-400 transition-colors rounded"
                                title="WhatsApp"
                              >
                                <MessageCircle className="w-4 h-4" />
                              </a>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* Custom Payments Tab */}
        {activeTab === 'payments' && (
          <div className="border border-border overflow-x-auto">
            {customPayments.length === 0 ? (
              <div className="text-center py-12">
                <Calendar className="w-12 h-12 text-foreground-muted mx-auto mb-4" />
                <p className="text-foreground-muted">No custom payments found</p>
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-foreground-muted">Client</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-foreground-muted">Description</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-foreground-muted">Amount</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-foreground-muted">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-foreground-muted">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {customPayments.map((payment) => (
                    <tr key={payment.id} className="hover:bg-muted/30 transition-colors">
                      <td className="px-4 py-4">
                        <p className="font-medium">{payment.customer_name}</p>
                        <p className="text-xs text-foreground-muted">{payment.customer_email}</p>
                      </td>
                      <td className="px-4 py-4">
                        <p className="text-sm">{payment.description || '-'}</p>
                      </td>
                      <td className="px-4 py-4">
                        <span className="font-semibold text-primary">{formatIDR(payment.amount || 0)}</span>
                      </td>
                      <td className="px-4 py-4">
                        {getStatusBadge(payment.payment_status)}
                      </td>
                      <td className="px-4 py-4">
                        <p className="text-sm text-foreground-muted">
                          {payment.created_at ? new Date(payment.created_at).toLocaleString() : 'N/A'}
                        </p>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* CRM Tab */}
        {activeTab === 'crm' && (
          <div data-testid="crm-section">
            {/* CRM Header */}
            <div className="flex flex-col md:flex-row gap-4 mb-6 items-start md:items-center justify-between">
              <div className="flex flex-1 gap-3 w-full md:w-auto">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-foreground-muted" />
                  <input
                    type="text"
                    placeholder="Search clients..."
                    value={clientSearch}
                    onChange={(e) => setClientSearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 bg-muted border border-border text-foreground focus:border-primary focus:outline-none text-sm"
                    data-testid="crm-search"
                  />
                </div>
                <select
                  value={clientFilter}
                  onChange={(e) => setClientFilter(e.target.value)}
                  className="px-3 py-2.5 bg-muted border border-border text-foreground text-sm focus:outline-none"
                  data-testid="crm-filter"
                >
                  <option value="all">All Clients</option>
                  <option value="subscribers">Subscribers</option>
                  <option value="one_time">One-time</option>
                </select>
              </div>
              <button
                onClick={() => setShowExportModal(true)}
                className="px-4 py-2.5 bg-primary text-primary-foreground font-semibold text-sm hover:scale-[0.98] transition-transform flex items-center gap-2"
                data-testid="export-clients-btn"
              >
                <FileText className="w-4 h-4" />
                Export CSV
              </button>
            </div>

            {/* CRM Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="p-4 bg-muted/30 border border-border">
                <p className="text-xs text-foreground-muted uppercase tracking-wider mb-1">Total Clients</p>
                <p className="text-2xl font-bold">{clients.length}</p>
              </div>
              <div className="p-4 bg-primary/10 border border-primary/30">
                <p className="text-xs text-primary uppercase tracking-wider mb-1">Subscribers</p>
                <p className="text-2xl font-bold text-primary">{clients.filter(c => c.is_subscriber).length}</p>
              </div>
              <div className="p-4 bg-green-500/10 border border-green-500/30">
                <p className="text-xs text-green-400 uppercase tracking-wider mb-1">Revenue</p>
                <p className="text-2xl font-bold text-green-400">{formatIDR(clients.reduce((sum, c) => sum + (c.total_spent || 0), 0))}</p>
              </div>
              <div className="p-4 bg-cyan-500/10 border border-cyan-500/30">
                <p className="text-xs text-cyan-400 uppercase tracking-wider mb-1">Avg Bookings</p>
                <p className="text-2xl font-bold text-cyan-400">{clients.length > 0 ? (clients.reduce((sum, c) => sum + c.bookings_count, 0) / clients.length).toFixed(1) : 0}</p>
              </div>
            </div>

            {/* Clients Table */}
            <div className="border border-border overflow-x-auto">
              {(() => {
                const filtered = clients.filter(c => {
                  const matchSearch = !clientSearch || 
                    c.name.toLowerCase().includes(clientSearch.toLowerCase()) ||
                    c.email.toLowerCase().includes(clientSearch.toLowerCase()) ||
                    (c.phone || '').includes(clientSearch);
                  const matchFilter = clientFilter === 'all' || 
                    (clientFilter === 'subscribers' && c.is_subscriber) ||
                    (clientFilter === 'one_time' && !c.is_subscriber);
                  return matchSearch && matchFilter;
                });
                
                return filtered.length === 0 ? (
                  <div className="text-center py-12">
                    <User className="w-12 h-12 text-foreground-muted mx-auto mb-4" />
                    <p className="text-foreground-muted">No clients found</p>
                  </div>
                ) : (
                  <table className="w-full">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-foreground-muted">Client</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-foreground-muted">Phone</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-foreground-muted">Bookings</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-foreground-muted">Total Spent</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-foreground-muted">Services</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-foreground-muted">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-foreground-muted">Last Activity</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {filtered.map((client, idx) => (
                        <tr key={idx} className="hover:bg-muted/30 transition-colors">
                          <td className="px-4 py-4">
                            <p className="font-medium">{client.name || '-'}</p>
                            <p className="text-xs text-foreground-muted">{client.email}</p>
                          </td>
                          <td className="px-4 py-4 text-sm">{client.phone || '-'}</td>
                          <td className="px-4 py-4">
                            <span className="font-semibold">{client.bookings_count}</span>
                          </td>
                          <td className="px-4 py-4">
                            <span className="font-semibold text-green-400">{formatIDR(client.total_spent || 0)}</span>
                          </td>
                          <td className="px-4 py-4">
                            <div className="flex flex-wrap gap-1">
                              {client.services.length > 0 ? client.services.map((s, i) => (
                                <span key={i} className="px-2 py-0.5 bg-primary/10 text-primary text-xs border border-primary/20">
                                  {s.replace('_', ' ')}
                                </span>
                              )) : (
                                <span className="text-xs text-foreground-muted">Studio booking</span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            {client.is_subscriber ? (
                              <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-primary/10 text-primary border border-primary/20">
                                <CheckCircle className="w-3 h-3" /> Subscriber
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-muted text-foreground-muted border border-border">
                                One-time
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-4">
                            <p className="text-sm text-foreground-muted">
                              {client.last_booking ? new Date(client.last_booking).toLocaleDateString() : '-'}
                            </p>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                );
              })()}
            </div>
          </div>
        )}

        {/* Edit/View Modal */}
        {showEditModal && selectedBooking && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-background border border-border w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6 border-b border-border flex items-center justify-between">
                <h2 className="text-xl font-heading font-semibold">
                  {selectedBooking._isInquiry ? 'Service Inquiry Details' : 'Booking Details'}
                </h2>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="p-2 hover:bg-muted rounded"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="p-6 space-y-6">
                {/* Client Info */}
                <div className="bg-muted/30 p-4 rounded">
                  <h3 className="text-sm font-semibold uppercase tracking-wider text-primary mb-3 flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Client Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-foreground-muted" />
                      <span className="font-semibold">{selectedBooking._isInquiry ? selectedBooking.full_name : selectedBooking.customer_name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-foreground-muted" />
                      <a href={`mailto:${selectedBooking._isInquiry ? selectedBooking.email : selectedBooking.customer_email}`} className="hover:text-primary transition-colors">
                        {selectedBooking._isInquiry ? selectedBooking.email : selectedBooking.customer_email}
                      </a>
                    </div>
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-foreground-muted" />
                      <a href={`tel:${selectedBooking._isInquiry ? selectedBooking.phone : selectedBooking.customer_phone}`} className="hover:text-primary transition-colors">
                        {selectedBooking._isInquiry ? selectedBooking.phone : selectedBooking.customer_phone}
                      </a>
                    </div>
                    <div className="flex items-center gap-2">
                      <MessageCircle className="w-4 h-4 text-foreground-muted" />
                      <span className="capitalize">{selectedBooking.contact_method || 'WhatsApp'}</span>
                      {(selectedBooking.contact_method === 'whatsapp') && (
                        <a 
                          href={`https://wa.me/${(selectedBooking._isInquiry ? selectedBooking.phone : selectedBooking.customer_phone)?.replace(/[^0-9]/g, '')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs px-2 py-1 bg-green-500/20 text-green-400 rounded hover:bg-green-500/30 transition-colors"
                        >
                          Open WhatsApp
                        </a>
                      )}
                    </div>
                  </div>
                </div>

              {selectedBooking._isInquiry ? (
                <>
                  {/* Service Inquiry Details */}
                  <div className="bg-primary/5 border border-primary/20 p-4 rounded">
                    <h3 className="text-sm font-semibold uppercase tracking-wider text-primary mb-3">Service Details</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-foreground-muted text-xs uppercase mb-1">Service Type</p>
                        <p className="font-bold text-lg">{serviceNames[selectedBooking.service_type] || selectedBooking.service_type}</p>
                      </div>
                      <div>
                        <p className="text-foreground-muted text-xs uppercase mb-1">Selected Pack</p>
                        <p className="font-bold text-lg text-primary">{selectedBooking.selected_pack || 'Not specified'}</p>
                      </div>
                      <div>
                        <p className="text-foreground-muted text-xs uppercase mb-1">Preferred Date</p>
                        <p className="font-semibold">{selectedBooking.preferred_date ? formatDate(selectedBooking.preferred_date) : 'TBD'}</p>
                      </div>
                      <div>
                        <p className="text-foreground-muted text-xs uppercase mb-1">Preferred Time</p>
                        <p className="font-semibold">{selectedBooking.preferred_time || 'TBD'}</p>
                      </div>
                    </div>
                    
                    {selectedBooking.time_slots && selectedBooking.time_slots.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-primary/20">
                        <p className="text-foreground-muted text-xs uppercase mb-2">Time Slots</p>
                        <div className="flex flex-wrap gap-2">
                          {selectedBooking.time_slots.map((slot, idx) => (
                            <span key={idx} className="px-3 py-1 bg-primary/10 text-primary text-sm font-medium rounded">
                              {slot}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Studio Config */}
                  {selectedBooking.studio_config && Object.keys(selectedBooking.studio_config).length > 0 && (
                    <div className="bg-amber-500/5 border border-amber-500/20 p-4 rounded">
                      <h3 className="text-sm font-semibold uppercase tracking-wider text-amber-400 mb-3 flex items-center gap-2">
                        <MapPin className="w-4 h-4" />
                        Studio Setup
                      </h3>
                      <pre className="text-xs text-foreground-muted bg-muted p-3 rounded overflow-auto">
                        {JSON.stringify(selectedBooking.studio_config, null, 2)}
                      </pre>
                    </div>
                  )}

                  {/* Production Options */}
                  {selectedBooking.production_options && (
                    <div className="bg-amber-500/5 border border-amber-500/20 p-4 rounded">
                      <h3 className="text-sm font-semibold uppercase tracking-wider text-amber-400 mb-3 flex items-center gap-2">
                        <Camera className="w-4 h-4" />
                        Production Options
                      </h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="text-center p-3 bg-background rounded">
                          <Camera className="w-6 h-6 mx-auto mb-2 text-primary" />
                          <p className="text-2xl font-bold">{selectedBooking.production_options.extra_cameras || 0}</p>
                          <p className="text-xs text-foreground-muted">Extra Cameras</p>
                        </div>
                        <div className="text-center p-3 bg-background rounded">
                          <Mic className="w-6 h-6 mx-auto mb-2 text-primary" />
                          <p className="text-2xl font-bold">{selectedBooking.production_options.extra_microphones || 0}</p>
                          <p className="text-xs text-foreground-muted">Extra Mics</p>
                        </div>
                        <div className="text-center p-3 bg-background rounded">
                          <Film className="w-6 h-6 mx-auto mb-2 text-primary" />
                          <p className="text-sm font-bold">{selectedBooking.production_options.video_editing?.enabled ? 'Yes' : 'No'}</p>
                          <p className="text-xs text-foreground-muted">Video Editing</p>
                        </div>
                        <div className="text-center p-3 bg-background rounded">
                          <Clapperboard className="w-6 h-6 mx-auto mb-2 text-primary" />
                          <p className="text-2xl font-bold">{selectedBooking.production_options.reels?.length || 0}</p>
                          <p className="text-xs text-foreground-muted">Reels</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Addons */}
                  {selectedBooking.addons && selectedBooking.addons.length > 0 && (
                    <div className="bg-muted/30 p-4 rounded">
                      <h3 className="text-sm font-semibold uppercase tracking-wider text-foreground-muted mb-3">Add-ons</h3>
                      <div className="flex flex-wrap gap-2">
                        {selectedBooking.addons.map((addon, idx) => (
                          <span key={idx} className="px-3 py-1 bg-primary/10 text-primary text-sm rounded">
                            {addon}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Notes */}
                  {selectedBooking.additional_notes && (
                    <div className="bg-muted/30 p-4 rounded">
                      <h3 className="text-sm font-semibold uppercase tracking-wider text-foreground-muted mb-3">Additional Notes</h3>
                      <p className="text-foreground">{selectedBooking.additional_notes}</p>
                    </div>
                  )}

                  {/* Payment Info */}
                  <div className="bg-muted/30 p-4 rounded">
                    <h3 className="text-sm font-semibold uppercase tracking-wider text-foreground-muted mb-3">Payment</h3>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-foreground-muted text-sm">Total amount</p>
                        <p className="text-3xl font-bold text-primary">{formatIDR(selectedBooking.total_price || 0)}</p>
                        <p className="text-xs text-foreground-muted mt-1">
                          Method: {selectedBooking.payment_method === 'pay_now' ? 'Online (Stripe)' : 'Pay Later'}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-foreground-muted text-sm mb-2">Status</p>
                        {getStatusBadge(selectedBooking.payment_status || selectedBooking.status)}
                      </div>
                    </div>
                  </div>

                  {/* Status Update */}
                  <div>
                    <h3 className="text-sm font-semibold uppercase tracking-wider text-foreground-muted mb-3">Update Status</h3>
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => updateInquiryStatus(selectedBooking.id, { status: 'confirmed', payment_status: 'paid' })}
                        className="px-4 py-2 border border-border hover:border-green-500 hover:text-green-400 transition-colors"
                      >
                        <CheckCircle className="w-4 h-4 inline mr-2" />
                        Confirm & Paid
                      </button>
                      <button
                        onClick={() => updateInquiryStatus(selectedBooking.id, { status: 'confirmed' })}
                        className="px-4 py-2 border border-border hover:border-blue-500 hover:text-blue-400 transition-colors"
                      >
                        <CheckCircle className="w-4 h-4 inline mr-2" />
                        Confirm
                      </button>
                      <button
                        onClick={() => updateInquiryStatus(selectedBooking.id, { status: 'cancelled', payment_status: 'cancelled' })}
                        className="px-4 py-2 border border-border hover:border-red-500 hover:text-red-400 transition-colors"
                      >
                        <XCircle className="w-4 h-4 inline mr-2" />
                        Cancel
                      </button>
                    </div>
                  </div>

                  <div className="text-center text-xs text-foreground-muted pt-4 border-t border-border">
                    <p>Inquiry ID: {selectedBooking.id}</p>
                    <p>Created: {selectedBooking.created_at ? new Date(selectedBooking.created_at).toLocaleString() : 'N/A'}</p>
                  </div>
                </>
              ) : (
                <>

                {/* Session & Studio Info */}
                <div className="bg-primary/5 border border-primary/20 p-4 rounded">
                  <h3 className="text-sm font-semibold uppercase tracking-wider text-primary mb-3 flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    Session Setup - TO PREPARE
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-foreground-muted text-xs uppercase mb-1">Studio</p>
                      <p className="font-bold text-lg">{selectedBooking.studio_id === 'solo-studio' ? 'Solo Studio' : 'Podcast Studio'}</p>
                    </div>
                    <div>
                      <p className="text-foreground-muted text-xs uppercase mb-1">Background</p>
                      <p className="font-bold text-lg text-primary">
                        {selectedBooking.background_id ? (
                          selectedBooking.background_id === 'black' ? 'Professional Black' :
                          selectedBooking.background_id === 'brown' ? 'Warm Brown' :
                          selectedBooking.background_id === 'corporate' ? 'Elegant Corporate' :
                          selectedBooking.background_id === 'tropical' ? 'Tropical Bali' :
                          selectedBooking.background_id
                        ) : 'Not specified'}
                      </p>
                    </div>
                    <div>
                      <p className="text-foreground-muted text-xs uppercase mb-1">Date</p>
                      <p className="font-semibold">{formatDate(selectedBooking.date)}</p>
                    </div>
                    <div>
                      <p className="text-foreground-muted text-xs uppercase mb-1">Time</p>
                      <p className="font-semibold">{selectedBooking.start_time} ({selectedBooking.duration_hours}h)</p>
                    </div>
                  </div>
                  
                  {selectedBooking.time_slots && selectedBooking.time_slots.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-primary/20">
                      <p className="text-foreground-muted text-xs uppercase mb-2">All Time Slots</p>
                      <div className="flex flex-wrap gap-2">
                        {selectedBooking.time_slots.map((slot, idx) => (
                          <span key={idx} className="px-3 py-1 bg-primary/10 text-primary text-sm font-medium rounded">
                            {formatDate(slot.date)} at {slot.start_time}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Equipment to Prepare */}
                <div className="bg-amber-500/5 border border-amber-500/20 p-4 rounded">
                  <h3 className="text-sm font-semibold uppercase tracking-wider text-amber-400 mb-3 flex items-center gap-2">
                    <Camera className="w-4 h-4" />
                    Equipment to Prepare
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center p-3 bg-background rounded">
                      <Camera className="w-6 h-6 mx-auto mb-2 text-primary" />
                      <p className="text-2xl font-bold">{(selectedBooking.options?.cameras || 2) + (selectedBooking.options?.extra_cameras || 0)}</p>
                      <p className="text-xs text-foreground-muted">Cameras 4K</p>
                      {selectedBooking.options?.extra_cameras > 0 && (
                        <p className="text-xs text-amber-400 mt-1">+{selectedBooking.options.extra_cameras} extra</p>
                      )}
                    </div>
                    <div className="text-center p-3 bg-background rounded">
                      <Mic className="w-6 h-6 mx-auto mb-2 text-primary" />
                      <p className="text-2xl font-bold">{(selectedBooking.options?.microphones || 2) + (selectedBooking.options?.extra_microphones || 0)}</p>
                      <p className="text-xs text-foreground-muted">Microphones</p>
                      {selectedBooking.options?.extra_microphones > 0 && (
                        <p className="text-xs text-amber-400 mt-1">+{selectedBooking.options.extra_microphones} extra</p>
                      )}
                    </div>
                    <div className="text-center p-3 bg-background rounded">
                      <Clock className="w-6 h-6 mx-auto mb-2 text-primary" />
                      <p className="text-2xl font-bold">{selectedBooking.duration_hours}h</p>
                      <p className="text-xs text-foreground-muted">Duration</p>
                    </div>
                    <div className="text-center p-3 bg-background rounded">
                      <Calendar className="w-6 h-6 mx-auto mb-2 text-primary" />
                      <p className="text-2xl font-bold">{selectedBooking.time_slots?.length || 1}</p>
                      <p className="text-xs text-foreground-muted">Slot(s)</p>
                    </div>
                  </div>
                </div>

                {/* Post-Production Services */}
                <div className="bg-muted/30 p-4 rounded">
                  <h3 className="text-sm font-semibold uppercase tracking-wider text-foreground-muted mb-3 flex items-center gap-2">
                    <Film className="w-4 h-4" />
                    Post-Production Services
                  </h3>
                  
                  {/* Video Editing */}
                  {selectedBooking.options?.video_editing?.enabled ? (
                    <div className="mb-4 p-3 bg-green-500/10 border border-green-500/20 rounded">
                      <div className="flex items-center gap-2 mb-2">
                        <CheckCircle className="w-4 h-4 text-green-400" />
                        <span className="font-semibold text-green-400">Full Video Editing - INCLUDED</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <span className="text-foreground-muted">Duration:</span>{' '}
                          <span className="font-medium">
                            {selectedBooking.options.video_editing.isCustomQuote ? 'Custom (On Quote)' : 
                             selectedBooking.options.video_editing.duration === '30' ? '30 minutes' :
                             selectedBooking.options.video_editing.duration === '60' ? '60 minutes' : 
                             selectedBooking.options.video_editing.duration || 'Not specified'}
                          </span>
                        </div>
                        <div>
                          <span className="text-foreground-muted">Cameras:</span>{' '}
                          <span className="font-medium">
                            {selectedBooking.options.video_editing.cameras?.replace('_', ' ') || 'Not specified'}
                          </span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="mb-4 p-3 bg-muted rounded">
                      <div className="flex items-center gap-2">
                        <XCircle className="w-4 h-4 text-foreground-muted" />
                        <span className="text-foreground-muted">No video editing requested</span>
                      </div>
                    </div>
                  )}

                  {/* Reels */}
                  {selectedBooking.options?.reels && selectedBooking.options.reels.length > 0 ? (
                    <div className="p-3 bg-green-500/10 border border-green-500/20 rounded">
                      <div className="flex items-center gap-2 mb-2">
                        <Clapperboard className="w-4 h-4 text-green-400" />
                        <span className="font-semibold text-green-400">
                          Reels Editing - {selectedBooking.options.reels.length} reel(s) INCLUDED
                        </span>
                      </div>
                      <div className="space-y-2">
                        {selectedBooking.options.reels.map((reel, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-sm bg-background/50 p-2 rounded">
                            <span className="w-6 h-6 bg-primary/20 text-primary rounded-full flex items-center justify-center text-xs font-bold">
                              {idx + 1}
                            </span>
                            <span className="capitalize font-medium">
                              {reel.type === 'simple' ? 'Simple Cut' :
                               reel.type === 'subtitles' ? 'With Subtitles' :
                               reel.type === 'motion_design' ? 'Motion Design' :
                               reel.type === 'premium' ? 'Premium' : reel.type}
                            </span>
                            {reel.coverDesign && (
                              <span className="text-xs px-2 py-0.5 bg-primary/20 text-primary rounded">+ Cover Design</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="p-3 bg-muted rounded">
                      <div className="flex items-center gap-2">
                        <XCircle className="w-4 h-4 text-foreground-muted" />
                        <span className="text-foreground-muted">No reels requested</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Payment Info */}
                <div className="bg-muted/30 p-4 rounded">
                  <h3 className="text-sm font-semibold uppercase tracking-wider text-foreground-muted mb-3">Payment</h3>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-foreground-muted text-sm">Total amount</p>
                      <p className="text-3xl font-bold text-primary">{formatIDR(selectedBooking.total_price)}</p>
                      <p className="text-xs text-foreground-muted mt-1">
                        Payment method: {selectedBooking.payment_method === 'pay_later' ? 'Pay Later (Bank/Cash)' : 'Online (Stripe)'}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-foreground-muted text-sm mb-2">Status</p>
                      {getStatusBadge(selectedBooking.payment_status)}
                    </div>
                  </div>
                </div>

                {/* Status Update */}
                <div>
                  <h3 className="text-sm font-semibold uppercase tracking-wider text-foreground-muted mb-3">Update Status</h3>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => updateBookingStatus(selectedBooking.id, 'paid')}
                      className={`px-4 py-2 border transition-colors ${
                        selectedBooking.payment_status === 'paid'
                          ? 'bg-green-500/20 border-green-500 text-green-400'
                          : 'border-border hover:border-green-500 hover:text-green-400'
                      }`}
                    >
                      <CheckCircle className="w-4 h-4 inline mr-2" />
                      Mark as Paid
                    </button>
                    <button
                      onClick={() => updateBookingStatus(selectedBooking.id, 'pending_payment')}
                      className={`px-4 py-2 border transition-colors ${
                        selectedBooking.payment_status === 'pending_payment'
                          ? 'bg-blue-500/20 border-blue-500 text-blue-400'
                          : 'border-border hover:border-blue-500 hover:text-blue-400'
                      }`}
                    >
                      <Clock className="w-4 h-4 inline mr-2" />
                      Pay Later
                    </button>
                    <button
                      onClick={() => updateBookingStatus(selectedBooking.id, 'cancelled')}
                      className={`px-4 py-2 border transition-colors ${
                        selectedBooking.payment_status === 'cancelled'
                          ? 'bg-red-500/20 border-red-500 text-red-400'
                          : 'border-border hover:border-red-500 hover:text-red-400'
                      }`}
                    >
                      <XCircle className="w-4 h-4 inline mr-2" />
                      Cancel
                    </button>
                  </div>
                </div>

                {/* Booking ID & Created */}
                <div className="text-center text-xs text-foreground-muted pt-4 border-t border-border">
                  <p>Booking ID: {selectedBooking.id}</p>
                  <p>Created: {selectedBooking.created_at ? new Date(selectedBooking.created_at).toLocaleString() : 'N/A'}</p>
                </div>
                </>
              )}
              </div>
            </motion.div>
          </div>
        )}

        {/* Export Clients Modal */}
        {showExportModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="w-full max-w-md bg-background border border-border p-6"
            >
              <h3 className="text-xl font-heading font-semibold mb-2">Export Client Data</h3>
              <p className="text-sm text-foreground-muted mb-6">
                Entrez le code de confirmation pour télécharger les données clients en CSV.
              </p>
              <input
                type="password"
                value={exportCode}
                onChange={(e) => setExportCode(e.target.value)}
                placeholder="Code de confirmation"
                className="w-full px-4 py-3 bg-muted border border-border text-foreground focus:border-primary focus:outline-none mb-4"
                data-testid="export-code-input"
                onKeyDown={(e) => e.key === 'Enter' && handleExportClients()}
              />
              <div className="flex gap-3">
                <button
                  onClick={() => { setShowExportModal(false); setExportCode(''); }}
                  className="flex-1 py-2 border border-border text-foreground-muted hover:text-foreground transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={handleExportClients}
                  disabled={exporting}
                  className="flex-1 py-2 bg-primary text-primary-foreground font-semibold hover:scale-[0.98] transition-transform flex items-center justify-center gap-2 disabled:opacity-60"
                  data-testid="confirm-export-btn"
                >
                  {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                  {exporting ? 'Export...' : 'Télécharger'}
                </button>
              </div>
            </motion.div>
          </div>
        )}

        {/* Cancel Booking Confirmation Modal */}
        {showCancelModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="w-full max-w-sm bg-background border border-border p-6 text-center"
            >
              <XCircle className="w-12 h-12 text-amber-400 mx-auto mb-4" />
              <h3 className="text-lg font-heading font-semibold mb-2">Are you sure you want to cancel?</h3>
              <p className="text-sm text-foreground-muted mb-6">This action cannot be undone.</p>
              <div className="flex gap-3">
                <button
                  onClick={() => { setShowCancelModal(false); setCancelBookingId(null); }}
                  className="flex-1 py-2.5 border border-border text-foreground-muted hover:text-foreground hover:border-foreground transition-colors"
                  data-testid="cancel-booking-dismiss"
                >
                  Go back
                </button>
                <button
                  onClick={confirmCancelBooking}
                  className="flex-1 py-2.5 bg-red-500 text-white font-semibold hover:bg-red-600 transition-colors"
                  data-testid="cancel-booking-confirm"
                >
                  Yes, cancel
                </button>
              </div>
            </motion.div>
          </div>
        )}

        {/* Reset Database Modal */}
        {showResetModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="w-full max-w-md bg-background border border-red-500/30 p-6"
            >
              <h3 className="text-xl font-heading font-semibold text-red-400 mb-2">Reset Database</h3>
              <p className="text-sm text-foreground-muted mb-6">
                Cette action supprimera toutes les données (bookings, factures, paiements). Entrez le code de confirmation.
              </p>
              <input
                type="password"
                value={resetCode}
                onChange={(e) => setResetCode(e.target.value)}
                placeholder="Code de confirmation"
                className="w-full px-4 py-3 bg-muted border border-border text-foreground focus:border-red-500 focus:outline-none mb-4"
                data-testid="reset-code-input"
                onKeyDown={(e) => e.key === 'Enter' && handleResetDB()}
              />
              <div className="flex gap-3">
                <button
                  onClick={() => { setShowResetModal(false); setResetCode(''); }}
                  className="flex-1 py-2 border border-border text-foreground-muted hover:text-foreground transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={handleResetDB}
                  disabled={resetting}
                  className="flex-1 py-2 bg-red-500 text-white font-semibold hover:bg-red-600 transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
                  data-testid="confirm-reset-btn"
                >
                  {resetting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  {resetting ? 'Suppression...' : 'Confirmer'}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
}
